import styled from '@emotion/styled';
import { ReactComponent as ArrowDownIcon } from 'assets/icons/arrow-down.svg';
import { TextStyle } from 'styles/variables/mixins';
import { Colors } from 'styles/variables/colors';
import { Variables } from 'styles/variables/variables';

const { white, darkGray, red, lightGray } = Colors;

export const MainContainer = styled.section<{
    reducedWidth: boolean;
    mobile: boolean;
}>`
    min-width: ${({ reducedWidth, mobile }) =>
        mobile ? '100%' : reducedWidth ? 'calc(100% - 380px)' : 'calc(100% - 345px)'};

    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin-bottom: 0;
    padding: ${({ mobile }) => (mobile ? '0rem' : '0.75rem 1rem')};
    min-height: 0;
    overflow-y: auto;
    max-height: calc(100vh - 80px);
    height: ${({ reducedWidth, mobile }) =>
        mobile ? 'auto' : reducedWidth ? 'calc(100vh - 80px)' : 'auto'};
    box-sizing: border-box;
`;

export const MainLayout = styled.div<{ mobile: boolean }>`
    margin: ${({ mobile }) => (mobile ? '56px 0 50px 0' : '56px 0 0 0')};
    width: 100%;
    height: 100%;
    box-sizing: border-box;
    display: flex;
    justify-content: center;
    position: relative;
`;

export const MainLayoutForDropdowns = styled.div<{ mobile: boolean }>`
    margin: ${({ mobile }) => (mobile ? '0px 0 50px 0' : '130px 0 0 0')};
    width: 100%;
    height: 100%;
    box-sizing: border-box;
    display: flex;
    padding: 0 20px;
    justify-content: center;
    position: relative;
    min-height: 100vh;
`;

export const Page = styled.div<{ mobile: boolean }>`
    width: 100%;
    background: ${({ mobile }) => (mobile ? '#F8F9FAFF' : white)};
    border-radius: 4px;
    overflow: hidden;
    box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.03);
    display: flex;
    flex-direction: ${({ mobile }) => (mobile ? 'column' : 'row')};
    position: relative;
    ${({ mobile }) => (mobile ? '' : 'min-height: calc(100vh - 80px);')}
`;

export const RightSection = styled.section<{
    increasedWidth: boolean;
    mobile: boolean;
    controlMargin: boolean;
}>`
    display: ${({ mobile }) => (mobile ? 'none' : 'flex')};
    flex-direction: column;
    align-self: flex-start;
    position: ${({ controlMargin }) => (controlMargin ? 'absolute' : 'relative')};
    top: ${({ controlMargin }) => (controlMargin ? '0' : 'auto')};
    right: ${({ controlMargin }) => (controlMargin ? '0' : 'auto')};
    padding-right: 1rem;
    padding-bottom: ${({ controlMargin }) => (controlMargin ? '0' : '1rem')};
    gap: 1rem;
    min-width: ${({ increasedWidth }) => (increasedWidth ? '380px' : '345px')};
    width: ${({ controlMargin, increasedWidth }) =>
        controlMargin ? (increasedWidth ? '380px' : '345px') : increasedWidth ? '35%' : '30%'};
    /* Match MainContainer's top padding (0.75rem) when in clientView/clientFlow
     * so the QuickActions card sits flush with the client profile / documents
     * table on the left. Other layouts keep the existing 2rem offset. */
    margin-top: ${({ controlMargin, increasedWidth }) =>
        controlMargin ? '' : increasedWidth ? '0.75rem' : '2rem'};
    box-sizing: border-box;
    height: fit-content;
    max-height: none;
    min-height: 0;
    overflow: visible;
`;

/**
 * Tight stack used to group Quick Actions, Engagement Score and Required
 * Documents in the right-rail. The surrounding `RightSection` keeps a 1rem
 * gap between this stack and the RightSidebar below it, while this wrapper
 * pulls the three cards visually closer to each other.
 */
export const CardStack = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
`;

export const OnboardingSearchbarContainer = styled.div<{ mobile: boolean }>`
    display: grid;
    gap: 5px;
    grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
    margin-right: 0.3rem;
    margin-left: 0.3rem;

    ${({ mobile }) =>
        !mobile &&
        `
        display: flex;
        flex-wrap: wrap;
        justify-content: space-between;
        > div {
            flex: 1;
            min-width: 200px;
            max-width: 289px;
            margin-bottom: 10px;
        }

        margin: 0px;
        column-gap: 0.5rem;
        padding: 1rem 1.5rem 1rem 1.5rem;
        border-radius: 8px;
        border: 1px solid #f3f4f6ff;
        box-shadow: 0px 10px 20px -15px #171a1f;
        max-height: 137px; 
    `}
`;

export const SearchbarContainer = styled.div<{ mobile: boolean }>`
    display: flex;
    justify-content: space-between;
    margin-right: ${({ mobile }) => (mobile ? '0.3rem' : '0')};
    ${({ mobile }) =>
        !mobile &&
        `
    column-gap: 1rem;
    padding: 1rem 1.5rem 1rem 1.5rem;
    border-radius: 8px;
    border: 1px solid #f3f4f6ff;
    box-shadow: 0px 10px 20px -15px #171a1f;
    height: 76px; 
    `}
`;

export const SearchbarContainerMobile = styled.div`
    display: flex;
    justify-content: space-between;
`;

export const SearchbarContainerMobileForDashboard = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
`;

export const TopContainer = styled.div<{ mobile?: boolean }>`
    width: 100%;
    display: flex;
    gap: 0.5rem;
    justify-content: space-around;
    margin-right: ${({ mobile }) => (mobile ? '0.3rem' : '0')};
`;

export const Footer = styled.section<{ mobile: boolean }>`
    display: ${({ mobile }) => (mobile ? 'flex' : 'none')};
    flex-direction: row;
    justify-content: center;
    padding: 0.5rem;
    background: #ffffffff;
    box-shadow: 0px 8px 17px #171a1f;
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 100;
`;

export const DropdownPrompts = styled.p`
    ${TextStyle({ fontSize: '15px', fontWeight: '500', color: Colors.gray })}
    text-align: center;
`;

// for left dropdown
type DropdownProps = {
    isActive?: boolean;
    mobile?: boolean;
    dashboard?: boolean;
    clientProfile?: boolean;
    customStyleForSearchbar?: boolean;
    agreementPage?: boolean;
    onboarding?: boolean;
    width?: string;
};

export const LeftDropdownContainer = styled.div`
    display: flex;
    flex-direction: column;
`;

export const LeftDropdownErrorMessage = styled.div`
    ${TextStyle({ fontSize: '14px', fontWeight: '400', color: red })};
    margin-left: 0.7rem;
`;

export const LeftDropdown = styled.div<DropdownProps>`
    ${TextStyle({ fontSize: '15px', fontWeight: '400' })}
    min-width: ${({
        clientProfile,
        dashboard,
        mobile,
        customStyleForSearchbar,
        agreementPage,
        onboarding,
        width,
    }) =>
        onboarding || (mobile && !clientProfile && !dashboard && !agreementPage)
            ? '100%'
            : (customStyleForSearchbar && (dashboard || clientProfile || agreementPage)) ||
                (mobile && (clientProfile || dashboard || agreementPage))
              ? '48%'
              : dashboard
                ? '32%'
                : width
                  ? width
                  : '289px'};
    max-width: ${({
        clientProfile,
        dashboard,
        mobile,
        customStyleForSearchbar,
        agreementPage,
        onboarding,
    }) =>
        onboarding || (mobile && !clientProfile && !dashboard && !agreementPage)
            ? '100%'
            : (customStyleForSearchbar && (dashboard || clientProfile || agreementPage)) ||
                (mobile && (clientProfile || dashboard || agreementPage))
              ? '48%'
              : dashboard
                ? '32%'
                : '289px'};
    height: 36px;
    border: ${(props) => (props.mobile ? '1px solid #8353E2FF' : '1px solid #00a9c0ff')};
    border-radius: 6px;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    line-height: 1.2;
    padding: 9px 12px;
    box-sizing: border-box;
    fill: ${darkGray};
    transition: all 110ms ease;
    background: ${white};
    outline: none;
    margin-left: ${(props) => (props.mobile ? '2px' : '0px')};
    box-shadow: ${(props) =>
        props.mobile ? '0px 0px 2px 3px #e8d4f4' : '0px 0px 2px 3px rgba(0, 169, 192, 0.4) '};
`;

export const DropDownMenu = styled.ul<{ dashboardMenuMobile?: boolean }>`
    width: ${({ dashboardMenuMobile }) => (dashboardMenuMobile ? '' : '289px')};
    border-radius: 8px;
    border: 1px solid #f3f4f6ff;
    box-shadow: 0px 10px 20px -15px #171a1f;
    padding: 0.5rem;
    max-height: 275px;
    overflow-y: auto;
`;

export const DropdownIcon = styled(ArrowDownIcon)`
    fill: inherit;
    max-width: 12px;
    min-width: 12px;
    margin-left: 9px;
`;

//for left menu item
type MenuItemProps = {
    margin?: string;
    marginRight?: string;
};

export const MenuItem = styled.li<MenuItemProps>`
    margin-left: ${(props) => (props.margin ? props.margin : '')};
    min-height: auto;
    text-decoration: none;
    padding-top: 0.1rem;
    padding-bottom: 0.1rem;
    border-radius: 4px;
    cursor: pointer;
    :hover {
        background-color: #e7def9ff;
        ${TextStyle({ fontSize: '15px', fontWeight: '600', color: '#6025d8ff' })};
    }
`;

export const Img = styled.img`
    margin-right: 5px;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: inline-block;
    object-fit: cover;
`;

export const Item = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    flex-wrap: nowrap;
    padding: 8px 12px;
    width: 100%;
`;

export const PlaceholderImg = styled.span`
    margin-right: 5px;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: inline-block;
    object-fit: cover;
`;

export const Text = styled.span`
    overflow: hidden;
    text-overflow: ellipsis;
    display: inline-block;
    /* display: flex;
    justify-content: space-between; */
    width: 100%;
    line-height: 1.2;
    white-space: nowrap;
`;

export const SelectionContainer = styled.div`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    display: inline-block;
`;

//for unread notifications dot
export const UnreadDot = styled.div`
    display: flex;
    align-self: flex-start;
    width: 8px;
    height: 8px;
    background-color: rgb(255, 166, 0);
    border-radius: 50%;
`;

//for longer explanation popovers
export const FakeInput = styled.div`
    ${TextStyle({ fontSize: '15px', fontWeight: '400', fontFamily: Variables.assistant })};
    padding: 20px;
    border: 1px solid ${lightGray};
    border-radius: 4px;
    margin-bottom: 1rem;
`;
